// frontend/src/components/admin/AdminAccountPortal.js
//
// Standalone portal at /admin-portal — for granular-role platform admin
// accounts (Tier 12, AdminUser in models/index.js), NOT the same thing
// as /admin (the superadmin panel, which is just an ERP staff Google
// account whose email is in the isPlatformAdmin list — see App.js).
// This is for admin team members who have neither an ERP staff account
// nor superadmin status, only a specific permission set granted by a
// superadmin from AdminPortal.js's "add admin" tab.
//
// Deliberately structured just like DeveloperPortal.js (one component,
// one of four states based on URL/localStorage) since
// routes/adminAccounts.js's own comment says it mirrors that pattern —
// same shape here keeps the two consistent for whoever reads both next.
//
// Note on scope: hub review moderation and Email Outbox are the two
// permission-gated feature panels currently wired here (see
// HubReviewsPanel/EmailOutboxPanel below), plus Data Deletion review
// (DataDeletionPanel) as of Tier 19. Not every permission in
// AVAILABLE_PERMISSIONS has a real screen yet (customer_care,
// developer_portal, billing, business_management) — those still just
// show up in "Your permissions" with nothing gated behind them.
import React, { useState, useEffect, useMemo } from 'react';
import * as api from '../../api';
import GoogleSignInButton from '../common/GoogleSignInButton';
import AdminPortal, { useInjectAdminCSS } from './AdminPortal';

// Mirrors AVAILABLE_PERMISSIONS in AdminPortal.js exactly — these are
// the only permission keys a superadmin can actually grant from the
// "add admin" tab, so this list has to stay in sync with that one.
const PERMISSION_LABELS = {
  customer_care:        'Customer Care — view and respond to buyer/business support inquiries',
  billing:              'Billing — view and manage subscription/payment issues',
  business_management:  'Business Management — view and manage native ERP businesses',
  email_outbox:         'Email Outbox Manager — see drafted emails and mark them sent after sending manually',
  data_deletion:         'Data Deletion Reviewer — review and approve/reject business data deletion requests',
};

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 420, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,.08)' }}>
        {children}
      </div>
    </div>
  );
}

function FieldInput(props) {
  return <input {...props} style={{ padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, ...(props.style || {}) }} />;
}

function PrimaryButton({ children, ...props }) {
  return (
    <button {...props} style={{ padding: '11px', borderRadius: 8, border: 'none', background: '#1e293b', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', ...(props.style || {}) }}>
      {children}
    </button>
  );
}

function ErrorBanner({ children }) {
  if (!children) return null;
  return <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{children}</div>;
}

function SetupForm({ setupToken, onDone }) {
  const [form, setForm] = useState({ name: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return setError('Password must be at least 8 characters.');
    if (form.password !== form.confirm) return setError("Passwords don't match.");
    setLoading(true); setError('');
    try {
      const data = await api.adminAccountSetup(setupToken, { password: form.password, name: form.name });
      api.saveAdminAccountToken(data.token);
      onDone();
    } catch (e) {
      setError(e.message); setLoading(false);
    }
  };

  return (
    <Shell>
      <h2 style={{ fontSize: 20, marginBottom: 4 }}>🛡️ Set up your admin account</h2>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>You've been added as a Wavelines platform admin. Set a password to finish setting up your account.</p>
      <ErrorBanner>{error}</ErrorBanner>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FieldInput type="text" placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <FieldInput type="password" placeholder="Password (min 8 characters)" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
        <FieldInput type="password" placeholder="Confirm password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
        <PrimaryButton type="submit" disabled={loading}>{loading ? 'Setting up…' : 'Complete setup'}</PrimaryButton>
      </form>
    </Shell>
  );
}

// Password-reset-by-email was removed (see docs/SESSION_CHANGELOG.md)
// — the Google sign-in button below is the recovery path now for
// anyone who loses their password, so there's no ResetForm/ForgotForm
// here anymore, and no /admin-portal/reset route to handle.
function LoginForm({ onDone }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = await api.adminAccountLogin(form.email, form.password);
      api.saveAdminAccountToken(data.token);
      onDone();
    } catch (e) {
      setError(e.message); setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setLoading(true); setError('');
    try {
      const data = await api.adminAccountGoogleLogin(credential);
      api.saveAdminAccountToken(data.token);
      onDone();
    } catch (e) {
      setError(e.message); setLoading(false);
    }
  };

  return (
    <Shell>
      <h2 style={{ fontSize: 20, marginBottom: 4 }}>🛡️ Admin Portal</h2>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Sign in with your Wavelines admin account.</p>
      <ErrorBanner>{error}</ErrorBanner>
      <GoogleSignInButton onCredential={handleGoogleCredential} onError={setError} width={320} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0' }}>
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        <span style={{ fontSize: 12, color: '#9ca3af' }}>or</span>
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
      </div>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <FieldInput type="email" placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
        <FieldInput type="password" placeholder="Password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
        <PrimaryButton type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign in'}</PrimaryButton>
      </form>
      <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16, textAlign: 'center' }}>
        Forgot your password? Sign in with Google above instead.
      </p>
    </Shell>
  );
}

function timeAgo(dateStr) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString();
}

function Stars({ rating }) {
  return <span style={{ color: '#f59e0b', fontSize: 13 }}>{'★'.repeat(rating)}{'☆'.repeat(5 - rating)}</span>;
}

// The first real feature actually gated behind requirePermission() —
// Email Outbox moderation, hitting routes/adminOutbox.js. Every drafted
// email queued by utils/emailOutbox.js shows up here for copying and
// manual sending.
function EmailOutboxPanel() {
  const [status, setStatus] = useState('pending');
  const [emails, setEmails] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = (s) => {
    api.getAdminOutbox(s).then(data => {
      setEmails(data.emails);
      setPendingCount(data.pendingCount);
    }).catch(e => setError(e.message));
  };

  React.useEffect(() => { load(status); }, [status]);

  const copyBody = (entry) => {
    navigator.clipboard.writeText(entry.text || entry.html || '');
    setCopiedId(entry._id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const doMarkSent = async (id) => {
    setBusyId(id); setError('');
    try { await api.markAdminOutboxSent(id); load(status); }
    catch (e) { setError(e.message); }
    setBusyId(null);
  };

  const doDismiss = async (id) => {
    setBusyId(id); setError('');
    try { await api.dismissAdminOutboxEntry(id); load(status); }
    catch (e) { setError(e.message); }
    setBusyId(null);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 22, border: '1px solid #e5e7eb', marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 15, margin: 0 }}>Email Outbox {pendingCount > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fffbeb', borderRadius: 999, padding: '2px 8px', marginLeft: 6 }}>{pendingCount} pending</span>}</h2>
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </select>
      </div>
      <p style={{ fontSize: 12, color: '#9ca3af', marginTop: -6, marginBottom: 14 }}>
        New messages use automatic delivery when enabled. Older drafts and failed sends remain here for review. Provider acceptance does not confirm inbox delivery.
      </p>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {emails === null ? (
        <p style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</p>
      ) : emails.length === 0 ? (
        <p style={{ fontSize: 13, color: '#9ca3af' }}>No {status !== 'all' ? status : ''} drafts.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {emails.map(entry => (
            <div key={entry._id} style={{ border: '1px solid #f3f4f6', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{entry.subject}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>To: {entry.to}{entry.businessName ? ` · ${entry.businessName}` : ''} · {timeAgo(entry.createdAt)}</div>
                </div>
              </div>
              <button onClick={() => setExpandedId(expandedId === entry._id ? null : entry._id)}
                style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}>
                {expandedId === entry._id ? 'Hide body' : 'Show body'}
              </button>
              {expandedId === entry._id && (
                <p style={{ fontSize: 12.5, color: '#374151', whiteSpace: 'pre-wrap', background: '#f9fafb', borderRadius: 8, padding: 10, margin: '4px 0 10px' }}>{`Delivery: ${entry.deliveryState || entry.status}\n${entry.lastError || ''}\n${entry.text || entry.html || ''}`}</p>
              )}
              {status === 'pending' && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button onClick={() => copyBody(entry)}
                    style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>
                    {copiedId === entry._id ? '✓ Copied' : '📋 Copy body'}
                  </button>
                  <button onClick={() => doMarkSent(entry._id)} disabled={busyId === entry._id || !['manual','failed','review'].includes(entry.deliveryState)}
                    style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #16a34a', background: '#fff', color: '#16a34a', cursor: 'pointer' }}>
                    {busyId === entry._id ? '…' : '✓ Mark sent'}
                  </button>
                  <button onClick={() => doDismiss(entry._id)} disabled={busyId === entry._id || !['manual','failed','review'].includes(entry.deliveryState)}
                    style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Data Deletion review — same "data_deletion" permission-gated pattern
// as HubReviewsPanel/EmailOutboxPanel above, hitting
// routes/adminDataDeletion.js. The approve action requires the same
// personally-verified-with-the-owner attestation as the superadmin
// path in AdminPortal.js — see that route's comment for why.
function DataDeletionPanel() {
  const [status, setStatus] = useState('pending');
  const [requests, setRequests] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveId, setApproveId] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const load = (s) => {
    api.getAdminDataDeletionRequests(s).then(data => {
      setRequests(data.requests);
      setPendingCount(data.pendingCount);
    }).catch(e => setError(e.message));
  };

  React.useEffect(() => { load(status); }, [status]);

  const doApprove = async () => {
    setBusyId(approveId); setError('');
    try {
      await api.approveAdminDataDeletionRequest(approveId, confirmed);
      setApproveId(null); setConfirmed(false);
      load(status);
    } catch (e) { setError(e.message); }
    setBusyId(null);
  };

  const doReject = async () => {
    setBusyId(rejectId); setError('');
    try {
      await api.rejectAdminDataDeletionRequest(rejectId, rejectReason);
      setRejectId(null); setRejectReason('');
      load(status);
    } catch (e) { setError(e.message); }
    setBusyId(null);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 22, border: '1px solid #e5e7eb', marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontSize: 15, margin: 0 }}>Data Deletion Requests {pendingCount > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', background: '#fef2f2', borderRadius: 999, padding: '2px 8px', marginLeft: 6 }}>{pendingCount} pending</span>}</h2>
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' }}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>
      <p style={{ fontSize: 12, color: '#9ca3af', marginTop: -6, marginBottom: 14 }}>
        Nothing is deleted until you personally verify the request with the business owner and confirm it below.
      </p>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {rejectId && (
        <div style={{ border: '1px solid #fca5a5', background: '#fef2f2', borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason (optional, shown to the business)"
            style={{ width: '100%', fontSize: 12.5, padding: '6px 10px', borderRadius: 6, border: '1px solid #e5e7eb', marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={doReject} disabled={busyId === rejectId}
              style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: 'none', background: '#b91c1c', color: '#fff', cursor: 'pointer' }}>Confirm reject</button>
            <button onClick={() => { setRejectId(null); setRejectReason(''); }}
              style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {approveId && (
        <div style={{ border: '1px solid #fca5a5', background: '#fef2f2', borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <p style={{ fontSize: 12.5, color: '#7f1d1d', margin: '0 0 8px' }}>
            This permanently deletes every order, payment, expense, inventory record, and related data for this business. User accounts are not affected. This cannot be undone.
          </p>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: '#374151', marginBottom: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
            I have personally verified this request with the business owner and confirm it's genuine.
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={doApprove} disabled={!confirmed || busyId === approveId}
              style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: 'none', background: confirmed ? '#b91c1c' : '#fca5a5', color: '#fff', cursor: confirmed ? 'pointer' : 'not-allowed' }}>
              {busyId === approveId ? 'Deleting…' : 'Approve & Permanently Delete'}
            </button>
            <button onClick={() => { setApproveId(null); setConfirmed(false); }}
              style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {requests === null ? (
        <p style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</p>
      ) : requests.length === 0 ? (
        <p style={{ fontSize: 13, color: '#9ca3af' }}>No {status !== 'all' ? status : ''} requests.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map(r => (
            <div key={r._id} style={{ border: '1px solid #f3f4f6', borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{r.businessName}</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                Requested by {r.requestedBy}{r.businessOwnerEmail ? ` · owner: ${r.businessOwnerEmail}` : ''} · {timeAgo(r.requestedAt)}
              </div>
              {r.status === 'rejected' && r.rejectionReason && <div style={{ fontSize: 12.5, color: '#7f1d1d', marginTop: 6 }}>Rejected: {r.rejectionReason}</div>}
              {r.status !== 'pending' && (
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{r.status === 'approved' ? '✓ Approved & deleted' : '✕ Rejected'} by {r.resolvedBy}</div>
              )}
              {r.status === 'pending' && (
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <button onClick={() => { setApproveId(r._id); setConfirmed(false); }} disabled={busyId === r._id}
                    style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #16a34a', background: '#fff', color: '#16a34a', cursor: 'pointer' }}>Approve</button>
                  <button onClick={() => { setRejectId(r._id); setRejectReason(''); }} disabled={busyId === r._id}
                    style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Unlike operational permissions, audit visibility is deliberately shared
// with every active administrator. It lets the team verify which admin took
// an action and when, without granting any additional operational power.
function AuditLogPanel() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState('');

  const load = () => {
    setError('');
    api.getAdminPortalAuditLog().then(data => setEntries(data.entries || [])).catch(e => setError(e.message));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="ap-panel">
      <div className="ap-panel-head"><h3>Platform Admin Activity</h3><button className="ap-refresh-btn" onClick={load}>↻ Refresh</button></div>
      {error && <div style={{ color: 'var(--ap-danger)', fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
      {entries === null ? <div className="ap-empty">Loading audit activity…</div> : entries.length === 0 ? <div className="ap-empty">No admin activity has been recorded yet.</div> : (
        <div style={{ display: 'grid', gap: 1 }}>
          {entries.map(entry => (
            <div key={entry._id} style={{ padding: '11px 0', borderBottom: '1px solid var(--ap-border)', fontSize: 12.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}><strong>{entry.action || entry.method || 'Admin action'}</strong><span style={{ color: entry.success === false ? 'var(--ap-danger)' : 'var(--ap-text-faint)' }}>{entry.success === false ? 'Failed' : 'Completed'}</span></div>
              <div style={{ color: 'var(--ap-text-dim)', marginTop: 3 }}>{entry.adminEmail || 'System'} · {entry.path || '—'} · {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// These labels, icons, and permissions mirror the super-admin console's
// navigation. A granular administrator sees only the workspaces their live
// permission record grants; the API independently enforces the same check.
const ROLE_NAV_ITEMS = [
  { key: 'overview', icon: '◆', label: 'Overview' },
  { key: 'outbox', icon: '✉', label: 'Email Outbox', permission: 'email_outbox' },
  { key: 'datadeletion', icon: '🗑️', label: 'Data Deletion', permission: 'data_deletion' },
  { key: 'auditlog', icon: '📜', label: 'Audit Log' },
];

function WorkspaceStat({ label, value, color, hero }) {
  return (
    <div className={`ap-stat ${hero ? 'ap-stat-hero' : ''}`} style={{ '--stat-color': color }}>
      <div className="ap-stat-label">{label}</div>
      <div className="ap-stat-value">{value}</div>
    </div>
  );
}

function AssignedPermissionNotice({ permissions, availablePermissions }) {
  const pending = permissions.filter(permission => !availablePermissions.includes(permission));
  if (!pending.length) return null;
  return (
    <div className="ap-panel">
      <div className="ap-panel-head"><h3>Assigned access</h3></div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ap-text-dim)', lineHeight: 1.6 }}>
        Your account also has: {pending.map(permission => PERMISSION_LABELS[permission] || permission).join(', ')}. These permissions remain protected on the server, but their dedicated workspaces have not been added to the role-based portal yet.
      </p>
    </div>
  );
}

function Dashboard({ onLogout }) {
  useInjectAdminCSS();
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('admin_portal_theme') !== 'light'; } catch (e) { return true; }
  });

  useEffect(() => {
    api.getAdminAccountMe().then(setMe).catch(e => {
      // A stale admin token should return its owner to LoginForm instead of
      // leaving the standalone portal on a dead-end error screen.
      if (e.status === 401) onLogout();
      else setError(e.message);
    });
  }, [onLogout]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    try { localStorage.setItem('admin_portal_theme', darkMode ? 'dark' : 'light'); } catch (e) {}
  }, [darkMode]);

  const permissions = me?.isSuperAdmin ? Object.keys(PERMISSION_LABELS) : (me?.permissions || []);
  const visibleNav = useMemo(() => ROLE_NAV_ITEMS.filter(item => !item.permission || permissions.includes(item.permission)), [permissions]);

  useEffect(() => {
    if (!me) return undefined;
    const selectFromHash = () => {
      const requested = window.location.hash.slice(1);
      setActiveTab(visibleNav.some(item => item.key === requested) ? requested : 'overview');
    };
    selectFromHash();
    window.addEventListener('hashchange', selectFromHash);
    return () => window.removeEventListener('hashchange', selectFromHash);
  }, [me, visibleNav]);

  const navigateToTab = (key) => {
    if (!visibleNav.some(item => item.key === key)) return;
    setActiveTab(key);
    if (window.location.hash !== `#${key}`) window.location.hash = key;
    setSidebarOpen(false);
  };

  if (!me) {
    return <Shell><div style={{ textAlign: 'center', color: '#9ca3af' }}>{error || 'Loading admin workspace…'}</div></Shell>;
  }

  // A granular account explicitly marked as superadmin gets the actual
  // full platform console, not a reduced imitation. Admin-account creation
  // and editing are intentionally removed from that console and blocked by
  // the backend, so this remains a delegated superadmin rather than the
  // platform owner.
  if (me.isSuperAdmin) {
    return <AdminPortal showAlert={(message) => window.alert(message)} hideAdminManagement onExit={onLogout} />;
  }

  const availablePermissions = ROLE_NAV_ITEMS.filter(item => item.permission).map(item => item.permission);
  const workspaceCount = visibleNav.length - 1;

  return (
    <div className="ap-root">
      {sidebarOpen && <div className="ap-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`ap-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="ap-sidebar-head">
          <div className="ap-eyebrow">Wavelines</div>
          <h1>Admin Console</h1>
          <p>{me.roleLabel || 'Role-based admin'} workspace</p>
        </div>
        <nav className="ap-nav">
          {visibleNav.map(item => (
            <button key={item.key} className={`ap-nav-item ${activeTab === item.key ? 'active' : ''}`} onClick={() => navigateToTab(item.key)}>
              <span className="ap-nav-icon">{item.icon}</span>{item.label}
            </button>
          ))}
        </nav>
        <div className="ap-sidebar-foot">
          <div className="ap-theme-toggle"><span>{darkMode ? 'Dark mode' : 'Light mode'}</span><button onClick={() => setDarkMode(mode => !mode)} title="Toggle theme">{darkMode ? '☀' : '☾'}</button></div>
          <button className="ap-exit-link" onClick={onLogout}>Sign out</button>
        </div>
      </aside>

      <div className="ap-main">
        <div className="ap-mobile-bar"><button onClick={() => setSidebarOpen(true)}>☰</button><strong>Admin Console</strong></div>
        <div className="ap-content">
          {activeTab === 'overview' && <>
            <div className="ap-page-head">
              <div><h2>Welcome back, {me.name || me.email}</h2><div className="ap-sub">Your role-based platform administration workspace.</div></div>
              <div className="ap-controls"><button className="ap-refresh-btn" onClick={() => window.location.reload()}>↻ Refresh</button></div>
            </div>
            {error && <div className="ap-panel" style={{ color: 'var(--ap-danger)' }}>{error}</div>}
            <div className="ap-stat-grid">
              <WorkspaceStat label="Assigned role" value={me.roleLabel || 'Admin'} color="#e94560" hero />
              <WorkspaceStat label="Active workspaces" value={workspaceCount} color="#3B82F6" />
              <WorkspaceStat label="Access status" value="Limited" color="#10B981" />
            </div>
            <div className="ap-panel">
              <div className="ap-panel-head"><h3>Your workspaces</h3></div>
              {workspaceCount ? visibleNav.filter(item => item.key !== 'overview').map(item => (
                <button key={item.key} className="ap-rank-row" onClick={() => navigateToTab(item.key)} style={{ width: '100%', background: 'transparent', color: 'inherit', borderLeft: 'none', borderRight: 'none', borderTop: 'none', textAlign: 'left' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span className="ap-nav-icon">{item.icon}</span><span>{item.label}</span></span><span style={{ color: 'var(--ap-text-faint)' }}>Open →</span>
                </button>
              )) : <div className="ap-empty"><div className="ap-empty-icon">🔒</div>No operational workspaces have been assigned yet.</div>}
            </div>
            <AssignedPermissionNotice permissions={permissions} availablePermissions={availablePermissions} />
          </>}
          {activeTab === 'outbox' && <><div className="ap-page-head"><div><h2>Email Outbox</h2><div className="ap-sub">Review and complete the messages assigned to your role.</div></div></div><EmailOutboxPanel /></>}
          {activeTab === 'datadeletion' && <><div className="ap-page-head"><div><h2>Data Deletion</h2><div className="ap-sub">Review requests carefully before taking permanent action.</div></div></div><DataDeletionPanel /></>}
          {activeTab === 'auditlog' && <><div className="ap-page-head"><div><h2>Audit Log</h2><div className="ap-sub">Shared record of platform administrator activity.</div></div></div><AuditLogPanel /></>}
        </div>
      </div>
    </div>
  );
}

export default function AdminAccountPortal() {
  const [loggedIn, setLoggedIn] = useState(!!api.getAdminAccountToken());
  const params = new URLSearchParams(window.location.search);
  const portalPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const setupToken = portalPath === '/admin-portal/setup' ? params.get('token') : null;

  const handleLogout = () => { api.clearAdminAccountToken(); setLoggedIn(false); };

  // An invitation token is the authority for this one-time setup. It must
  // win over any saved portal session: otherwise an expired or different
  // administrator's browser token sends the invitee to ordinary login before
  // they ever get a chance to create their password.
  if (setupToken) {
    return <SetupForm setupToken={setupToken} onDone={() => { window.location.href = '/admin-portal'; }} />;
  }
  if (!loggedIn) {
    return <LoginForm onDone={() => setLoggedIn(true)} />;
  }
  return <Dashboard onLogout={handleLogout} />;
}
